import { Destroyer, EOperatingSystem, filterNullish, IResponsiveConfiguration, isNullOrUndefined } from '@adaptiva/core/common';
import { ErrorHandlerService } from '@adaptiva/core/error-handler';
import { LogicalAnd, LogicalCondition, LogicalOr, NumberEquals } from '@adaptiva/core/filters';
import { OverlayService } from '@adaptiva/core/overlays';
import { UiService } from '@adaptiva/core/ui';
import { AdaptivaPackage, EJavaClassNames, IMemoryPipelineNode, MemoryPipelineNode, MemoryPipelineService } from '@adaptiva/data-service';
import { MemoryPipelineComponent } from '@adaptiva/widgets/memory-pipeline';
import { ObjectListComponent } from '@adaptiva/widgets/object-list';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, ViewChild } from '@angular/core';
import { BehaviorSubject, Subscription, switchMap, tap, timer } from 'rxjs';

@Component({
	standalone: false,
	selector: 'ada-memory-pipeline-page',
	templateUrl: './memory-pipeline-page.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class MemoryPipelinePageComponent extends Destroyer implements AfterViewInit {

	protected readonly publishedFilter: LogicalCondition = new LogicalAnd([
		new LogicalOr([
			new NumberEquals(
				'publicationStatus', AdaptivaPackage.PUBLISHED
			)
		])
	]);

	public readonly contentId: BehaviorSubject<number> = new BehaviorSubject(null);

	public readonly activeSelection: BehaviorSubject<IMemoryPipelineNode> = new BehaviorSubject(null);

	public readonly selectionDrawerResponsiveConfig: IResponsiveConfiguration = {
		SM: 1,
		MD: 1,
		LG: 1,
		XL: 1
	};

	public readonly packageClassName: EJavaClassNames = EJavaClassNames.AdaptivaPackage;

	@ViewChild('ContentList')
	private _contentList: ObjectListComponent;

	@ViewChild('MemoryPipeline')
	public memoryPipeline: MemoryPipelineComponent;

	public rootContentMemoryNodes: MemoryPipelineNode[];

	public ui: UiService = inject(UiService);

	public memoryPipelineService: MemoryPipelineService = inject(MemoryPipelineService);

	public overlayService: OverlayService = inject(OverlayService);

	public cd: ChangeDetectorRef = inject(ChangeDetectorRef);

	public errorHandler: ErrorHandlerService = inject(ErrorHandlerService);

	protected polling!: Subscription;

	public ngAfterViewInit(): void {
		if (isNullOrUndefined(this.contentId.getValue()))
			this._contentList.open();

		this.contentId.pipe(
			filterNullish(),
			this.takeUntilDestroyed(),
			switchMap((_contentId: long) => {
				return this.memoryPipelineService.getMemoryPipelineForContent(_contentId).pipe(
					filterNullish(),
					tap((_roots: IMemoryPipelineNode[]) => {
						this.rootContentMemoryNodes = _roots; // Update to handle multiple heads?
						this.cd.detectChanges();
						this.memoryPipeline?.loadScene();
						this.poll(_contentId);
					})
				);
			})
		).subscribe({
			error: (_error: Error) => {
				this.errorHandler.defaultErrorHandler(_error, 'MemoryPipelinePageComponent#ngAfterViewInit');
			}
		});
	}

	protected poll(_contentId: long): void {
		this.polling?.unsubscribe();
		this.polling = this.memoryPipelineService.getMemoryPipelineForContent(_contentId).pipe(
			filterNullish(),
			switchMap((_content: IMemoryPipelineNode[]) => {
				// some update model 
				this.rootContentMemoryNodes = _content;
				this.memoryPipeline.updateModel(_content);

				return timer(2000);
			}),
			this.takeUntilDestroyed(),
		).subscribe({
			next: () => {
				this.poll(_contentId);
			},
			error: (_error: Error) => {
				this.errorHandler.defaultErrorHandler(_error, 'MemoryPipelinePageComponent#poll');
			}
		});
	}

	public displayOsValue(_operatingSystem: EOperatingSystem): string {
		return EOperatingSystem.toDisplay(_operatingSystem);
	}

}
